import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PlusIcon,
  TrashIcon,
  DocumentIcon,
  LockClosedIcon,
  LockOpenIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  PlayCircleIcon,
  VideoCameraIcon,
  AcademicCapIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../Common/LoadingSpinner';

const CourseEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseCode: '',
    credits: 3,
    maxStudents: 30,
    fees: 0,
    category: '',
    level: 'Beginner',
    prerequisites: [''],
    materials: [],
    modules: []
  });

  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(null);
  const [lectureFormData, setLectureFormData] = useState({
    title: '',
    duration: '',
    type: 'video',
    videoSource: 'upload',
    videoUrl: '',
    liveType: 'zoom',
    liveLink: '',
    isFreePreview: false
  });

  const categories = [
    'Computer Science', 'Mathematics', 'Physics', 'Chemistry',
    'Biology', 'English', 'History', 'Arts', 'Business', 'Other'
  ];

  const fetchCourseDetails = async () => {
    try {
      const response = await axios.get(`/api/courses/${id}`);
      const course = response.data;

      setFormData({
        title: course.title || '',
        description: course.description || '',
        courseCode: course.courseCode || '',
        credits: course.credits || 3,
        maxStudents: course.maxStudents || 30,
        fees: course.fees || 0,
        category: course.category || '',
        level: course.level || 'Beginner',
        prerequisites: course.prerequisites?.length > 0 ? course.prerequisites : [''],
        materials: course.materials || [],
        modules: course.modules || []
      });

      // Auto-expand all modules for editing
      if (course.modules?.length > 0) {
        setExpandedModules(course.modules.map((_, index) => index));
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course details');
      navigate('/courses');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handlePrerequisiteChange = (index, value) => {
    const newPrerequisites = [...formData.prerequisites];
    newPrerequisites[index] = value;
    setFormData(prev => ({ ...prev, prerequisites: newPrerequisites }));
  };

  const addPrerequisite = () => {
    setFormData(prev => ({
      ...prev,
      prerequisites: [...prev.prerequisites, '']
    }));
  };

  const removePrerequisite = (index) => {
    const newPrerequisites = formData.prerequisites.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, prerequisites: newPrerequisites }));
  };

  // Material handling functions
  const addMaterial = () => {
    setFormData(prev => ({
      ...prev,
      materials: [...prev.materials, {
        title: '',
        type: 'document',
        description: '',
        isFree: false,
        file: null,
        url: ''
      }]
    }));
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, materials: newMaterials }));
  };

  const updateMaterial = (index, field, value) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = { ...newMaterials[index], [field]: value };
    setFormData(prev => ({ ...prev, materials: newMaterials }));
  };

  // Module handling functions
  const addModule = () => {
    const newModule = {
      title: '',
      description: '',
      lectures: []
    };
    setFormData(prev => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
    setExpandedModules(prev => [...prev, formData.modules.length]);
  };

  const removeModule = (index) => {
    const newModules = formData.modules.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, modules: newModules }));
    setExpandedModules(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const updateModule = (index, field, value) => {
    const newModules = [...formData.modules];
    newModules[index] = { ...newModules[index], [field]: value };
    setFormData(prev => ({ ...prev, modules: newModules }));
  };

  const toggleModule = (index) => {
    setExpandedModules(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Lecture handling functions
  const openLectureModal = (moduleIndex) => {
    setCurrentModuleIndex(moduleIndex);
    setShowLectureModal(true);
  };

  const closeLectureModal = () => {
    setShowLectureModal(false);
    setCurrentModuleIndex(null);
    setLectureFormData({
      title: '',
      duration: '',
      type: 'video',
      videoSource: 'upload',
      videoUrl: '',
      liveType: 'zoom',
      liveLink: '',
      isFreePreview: false
    });
  };

  const handleLectureFormChange = (field, value) => {
    setLectureFormData(prev => ({ ...prev, [field]: value }));
  };

  const addLecture = () => {
    if (!lectureFormData.title.trim()) {
      toast.error('Lecture title is required');
      return;
    }
    if (!lectureFormData.duration) {
      toast.error('Lecture duration is required');
      return;
    }

    const newLecture = { ...lectureFormData };
    const newModules = [...formData.modules];
    newModules[currentModuleIndex].lectures.push(newLecture);
    setFormData(prev => ({ ...prev, modules: newModules }));
    closeLectureModal();
    toast.success('Lecture added successfully');
  };

  const removeLecture = (moduleIndex, lectureIndex) => {
    const newModules = [...formData.modules];
    newModules[moduleIndex].lectures = newModules[moduleIndex].lectures.filter((_, i) => i !== lectureIndex);
    setFormData(prev => ({ ...prev, modules: newModules }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File size must be less than 100MB');
      return;
    }

    try {
      setUploadingFiles(prev => [...prev, index]);

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('type', 'course-material');

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = response.data.url || response.data.filePath;
      const updates = {
        url: fileUrl,
        filename: response.data.filename
      };

      if (!formData.materials[index].title) {
        updates.title = file.name.split('.')[0];
      }

      const newMaterials = [...formData.materials];
      newMaterials[index] = { ...newMaterials[index], ...updates };
      setFormData(prev => ({ ...prev, materials: newMaterials }));

      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFiles(prev => prev.filter(i => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate materials
      for (let i = 0; i < formData.materials.length; i++) {
        const material = formData.materials[i];

        if (!material.title.trim()) {
          toast.error(`Material ${i + 1}: Title is required`);
          setLoading(false);
          return;
        }
        if (material.type === 'link') {
          if (!material.url.trim()) {
            toast.error(`Material ${i + 1}: URL is required for link type`);
            setLoading(false);
            return;
          }
        } else {
          if (!material.url) {
            toast.error(`Material ${i + 1}: Please upload a file`);
            setLoading(false);
            return;
          }
          if (uploadingFiles.includes(i)) {
            toast.error(`Material ${i + 1}: File is still uploading, please wait`);
            setLoading(false);
            return;
          }
        }
      }

      const cleanedData = {
        ...formData,
        prerequisites: formData.prerequisites.filter(p => p.trim() !== ''),
        materials: formData.materials.map(material => ({
          title: material.title,
          type: material.type,
          url: material.url,
          filename: material.filename || '',
          description: material.description || '',
          isFree: material.isFree
        })),
        modules: formData.modules.map(module => ({
          title: module.title,
          description: module.description,
          lectures: module.lectures.map(lecture => ({
            title: lecture.title,
            duration: lecture.duration,
            type: lecture.type,
            videoSource: lecture.videoSource,
            videoUrl: lecture.videoUrl,
            liveType: lecture.liveType,
            liveLink: lecture.liveLink,
            isFreePreview: lecture.isFreePreview
          }))
        }))
      };

      await axios.put(`/api/courses/${id}`, cleanedData);
      toast.success('Course updated successfully!');
      navigate(`/courses/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update course');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/courses/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Course
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Course</h1>
        <p className="mt-2 text-gray-600">
          Update the course details below
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="input"
                placeholder="Introduction to Computer Science"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Code *
              </label>
              <input
                type="text"
                name="courseCode"
                required
                value={formData.courseCode}
                onChange={handleChange}
                className="input"
                placeholder="CS101"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                required
                value={formData.category}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Level *
              </label>
              <select
                name="level"
                required
                value={formData.level}
                onChange={handleChange}
                className="input"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credits *
              </label>
              <input
                type="number"
                name="credits"
                required
                min="1"
                value={formData.credits}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Students *
              </label>
              <input
                type="number"
                name="maxStudents"
                required
                min="1"
                value={formData.maxStudents}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Fees ($) *
              </label>
              <input
                type="number"
                name="fees"
                required
                min="0"
                value={formData.fees}
                onChange={handleChange}
                className="input"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              required
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className="input"
              placeholder="Describe what students will learn in this course..."
            />
          </div>
        </div>

        {/* Prerequisites */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Prerequisites</h2>
            <button
              type="button"
              onClick={addPrerequisite}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add
            </button>
          </div>

          <div className="space-y-3">
            {formData.prerequisites.map((prereq, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={prereq}
                  onChange={(e) => handlePrerequisiteChange(index, e.target.value)}
                  className="input flex-1"
                  placeholder="Enter prerequisite"
                />
                <button
                  type="button"
                  onClick={() => removePrerequisite(index)}
                  className="btn btn-danger btn-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Course Materials */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Course Materials</h2>
            <button
              type="button"
              onClick={addMaterial}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Material
            </button>
          </div>

          <div className="space-y-4">
            {formData.materials.map((material, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <DocumentIcon className="h-5 w-5 text-indigo-600" />
                    <span className="font-medium text-gray-900">Material {index + 1}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMaterial(index)}
                    className="btn btn-danger btn-sm"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={material.title}
                      onChange={(e) => updateMaterial(index, 'title', e.target.value)}
                      className="input text-sm"
                      placeholder="Material title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Type *
                    </label>
                    <select
                      value={material.type}
                      onChange={(e) => updateMaterial(index, 'type', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="document">Document/PDF</option>
                      <option value="video">Video</option>
                      <option value="note">Study Notes</option>
                      <option value="link">External Link</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={material.description}
                      onChange={(e) => updateMaterial(index, 'description', e.target.value)}
                      className="input text-sm"
                      rows="2"
                      placeholder="Brief description"
                    />
                  </div>

                  {material.type === 'link' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        URL *
                      </label>
                      <input
                        type="url"
                        value={material.url}
                        onChange={(e) => updateMaterial(index, 'url', e.target.value)}
                        className="input text-sm"
                        placeholder="https://example.com"
                        required
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Upload File *
                      </label>
                      <div className="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                        <div className="space-y-1 text-center">
                          <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                          <div className="flex text-xs text-gray-600">
                            <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                              <span>Upload</span>
                              <input
                                type="file"
                                className="sr-only"
                                accept={material.type === 'video' ? 'video/*' : '.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg'}
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    updateMaterial(index, 'file', file);
                                    handleFileUpload(index, file);
                                  }
                                }}
                              />
                            </label>
                          </div>
                          {uploadingFiles.includes(index) && (
                            <div className="text-xs text-blue-600">
                              <div className="animate-spin inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full mr-1"></div>
                              Uploading...
                            </div>
                          )}
                          {material.url && (
                            <p className="text-xs text-green-600 font-medium">✓ {material.filename || 'Uploaded'}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`material-free-${index}`}
                      checked={material.isFree}
                      onChange={(e) => updateMaterial(index, 'isFree', e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`material-free-${index}`} className="ml-2 flex items-center text-sm text-gray-700">
                      {material.isFree ? (
                        <LockOpenIcon className="h-4 w-4 mr-1 text-green-600" />
                      ) : (
                        <LockClosedIcon className="h-4 w-4 mr-1 text-gray-400" />
                      )}
                      Free Access
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Course Modules */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Course Modules</h2>
            <button
              type="button"
              onClick={addModule}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Module
            </button>
          </div>

          <div className="space-y-4">
            {formData.modules.map((module, moduleIndex) => (
              <div key={moduleIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1">
                      <button
                        type="button"
                        onClick={() => toggleModule(moduleIndex)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        {expandedModules.includes(moduleIndex) ? (
                          <ChevronUpIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                      <AcademicCapIcon className="h-5 w-5 text-indigo-600" />
                      <span className="font-medium text-gray-900">Module {moduleIndex + 1}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeModule(moduleIndex)}
                      className="btn btn-danger btn-sm"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-8">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Module Title *
                      </label>
                      <input
                        type="text"
                        value={module.title}
                        onChange={(e) => updateModule(moduleIndex, 'title', e.target.value)}
                        className="input text-sm"
                        placeholder="Introduction to the Course"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        value={module.description}
                        onChange={(e) => updateModule(moduleIndex, 'description', e.target.value)}
                        className="input text-sm"
                        placeholder="Brief description"
                      />
                    </div>
                  </div>
                </div>

                {expandedModules.includes(moduleIndex) && (
                  <div className="p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Lectures</h4>
                      <button
                        type="button"
                        onClick={() => openLectureModal(moduleIndex)}
                        className="btn btn-primary btn-sm flex items-center"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Lecture
                      </button>
                    </div>

                    <div className="space-y-2">
                      {module.lectures && module.lectures.length > 0 ? (
                        module.lectures.map((lecture, lectureIndex) => (
                          <div
                            key={lectureIndex}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              {lecture.type === 'video' ? (
                                <PlayCircleIcon className="h-5 w-5 text-indigo-600" />
                              ) : (
                                <VideoCameraIcon className="h-5 w-5 text-green-600" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{lecture.title}</p>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                  <span>{lecture.duration}m</span>
                                  <span>•</span>
                                  <span>{lecture.type === 'video' ? (
                                    lecture.videoSource === 'upload' ? 'Video Upload' : 'YouTube Link'
                                  ) : (
                                    `Live (${lecture.liveType})`
                                  )}</span>
                                  {lecture.isFreePreview && (
                                    <>
                                      <span>•</span>
                                      <span className="text-green-600 font-medium">Free Preview</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeLecture(moduleIndex, lectureIndex)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No lectures yet. Click "Add Lecture" to get started.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/courses/${id}`)}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || uploadingFiles.length > 0}
            className="btn btn-primary disabled:opacity-50"

          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Updating Course...
              </div>
            ) : (
              'Update Course'
            )}
          </button>
        </div>
      </form>

      {/* Lecture Modal */}
      {showLectureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add New Lecture</h3>
              <button
                type="button"
                onClick={closeLectureModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lecture Title *
                </label>
                <input
                  type="text"
                  value={lectureFormData.title}
                  onChange={(e) => handleLectureFormChange('title', e.target.value)}
                  className="input"
                  placeholder="Introduction to Variables"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (Minutes) *
                </label>
                <input
                  type="number"
                  min="0"
                  value={lectureFormData.duration}
                  onChange={(e) => handleLectureFormChange('duration', e.target.value)}
                  className="input"
                  placeholder="45"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lecture Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-indigo-500"
                    style={{
                      borderColor: lectureFormData.type === 'video' ? '#4F46E5' : '#E5E7EB',
                      backgroundColor: lectureFormData.type === 'video' ? '#EEF2FF' : 'white'
                    }}
                  >
                    <input
                      type="radio"
                      name="lectureType"
                      value="video"
                      checked={lectureFormData.type === 'video'}
                      onChange={(e) => handleLectureFormChange('type', e.target.value)}
                      className="h-4 w-4 text-indigo-600"
                    />
                    <PlayCircleIcon className="h-5 w-5 ml-3 mr-2 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-900">Video</span>
                  </label>
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-green-500"
                    style={{
                      borderColor: lectureFormData.type === 'live' ? '#10B981' : '#E5E7EB',
                      backgroundColor: lectureFormData.type === 'live' ? '#ECFDF5' : 'white'
                    }}
                  >
                    <input
                      type="radio"
                      name="lectureType"
                      value="live"
                      checked={lectureFormData.type === 'live'}
                      onChange={(e) => handleLectureFormChange('type', e.target.value)}
                      className="h-4 w-4 text-green-600"
                    />
                    <VideoCameraIcon className="h-5 w-5 ml-3 mr-2 text-green-600" />
                    <span className="text-sm font-medium text-gray-900">Live Session</span>
                  </label>
                </div>
              </div>

              {lectureFormData.type === 'video' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video Source *
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-indigo-500"
                        style={{
                          borderColor: lectureFormData.videoSource === 'upload' ? '#4F46E5' : '#E5E7EB',
                          backgroundColor: lectureFormData.videoSource === 'upload' ? '#EEF2FF' : 'white'
                        }}
                      >
                        <input
                          type="radio"
                          name="videoSource"
                          value="upload"
                          checked={lectureFormData.videoSource === 'upload'}
                          onChange={(e) => handleLectureFormChange('videoSource', e.target.value)}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">Upload Video</span>
                      </label>
                      <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-indigo-500"
                        style={{
                          borderColor: lectureFormData.videoSource === 'link' ? '#4F46E5' : '#E5E7EB',
                          backgroundColor: lectureFormData.videoSource === 'link' ? '#EEF2FF' : 'white'
                        }}
                      >
                        <input
                          type="radio"
                          name="videoSource"
                          value="link"
                          checked={lectureFormData.videoSource === 'link'}
                          onChange={(e) => handleLectureFormChange('videoSource', e.target.value)}
                          className="h-4 w-4 text-indigo-600"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">YouTube Link</span>
                      </label>
                    </div>
                  </div>

                  {lectureFormData.videoSource === 'link' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        YouTube URL *
                      </label>
                      <input
                        type="url"
                        value={lectureFormData.videoUrl}
                        onChange={(e) => handleLectureFormChange('videoUrl', e.target.value)}
                        className="input"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                  )}
                </div>
              )}

              {lectureFormData.type === 'live' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Live Session Platform *
                    </label>
                    <select
                      value={lectureFormData.liveType}
                      onChange={(e) => handleLectureFormChange('liveType', e.target.value)}
                      className="input"
                    >
                      <option value="zoom">Zoom</option>
                      <option value="teams">Microsoft Teams</option>
                      <option value="meet">Google Meet</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meeting Link *
                    </label>
                    <input
                      type="url"
                      value={lectureFormData.liveLink}
                      onChange={(e) => handleLectureFormChange('liveLink', e.target.value)}
                      className="input"
                      placeholder="https://zoom.us/j/..."
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="lecture-free-preview"
                  checked={lectureFormData.isFreePreview}
                  onChange={(e) => handleLectureFormChange('isFreePreview', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="lecture-free-preview" className="ml-2 text-sm text-gray-700">
                  Allow free preview (non-enrolled students can watch)
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeLectureModal}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addLecture}
                className="btn btn-primary"
              >
                Add Lecture
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseEdit;
